/**
 * Timekeeper title normalisation.
 *
 * `prof_type` arrives from the billing system as free text — roughly 160
 * distinct values in the Kahana Feld data, most appearing once. It is also
 * fractured: "Partner" is spread across nine spellings (PARTNER, MANAGING
 * PARTNER, Junior Partner, BRANCH OFFICE MANAGING PARTNER, …), so filtering on
 * a raw value returns 90 of 141 actual partners.
 *
 * This maps the raw text onto a small set of groups a firm would recognise,
 * so the Ratios title filter means what it says.
 */

export type ProfTitleGroup =
	| "Partner"
	| "Senior Counsel"
	| "Of Counsel"
	| "Associate"
	| "Law Clerk"
	| "Paralegal"
	| "Legal Support"
	| "Billing & Accounting"
	| "IT"
	| "HR & Recruiting"
	| "Marketing & Client Relations"
	| "Administration"
	| "Unspecified";

/**
 * Groups that enter billable time, in seniority order.
 *
 * Drives the filter dropdown ordering and which groups are offered by default.
 * Everyone else still appears under "All titles" — this decides prominence,
 * not visibility.
 */
export const BILLING_TITLE_GROUPS: ProfTitleGroup[] = [
	"Partner",
	"Senior Counsel",
	"Of Counsel",
	"Associate",
	"Law Clerk",
	"Paralegal",
	"Legal Support",
];

const NON_BILLING_TITLE_GROUPS: ProfTitleGroup[] = [
	"Billing & Accounting",
	"IT",
	"HR & Recruiting",
	"Marketing & Client Relations",
	"Administration",
	"Unspecified",
];

/** Every group, billing roles first. */
export const ALL_TITLE_GROUPS: ProfTitleGroup[] = [
	...BILLING_TITLE_GROUPS,
	...NON_BILLING_TITLE_GROUPS,
];

export function isBillingTitleGroup(group: ProfTitleGroup): boolean {
	return BILLING_TITLE_GROUPS.includes(group);
}

/**
 * Ordered match rules — first hit wins, so ordering carries the logic.
 *
 * Two orderings matter in particular:
 *  - PARALEGAL and LEGAL SUPPORT are tested before PARTNER and the generic
 *    manager/assistant fallthrough, so "PARALEGAL MANAGER" is a paralegal.
 *  - The executive-assistant rule precedes PARTNER, so "EXEC. DIR. TO THE
 *    NATIONAL MANAGING PARTNER" is administration rather than a partner.
 */
const RULES: Array<[RegExp, ProfTitleGroup]> = [
	// Assistants *to* partners, before the PARTNER rule can claim them.
	[/\bEXEC\b|DIR\.?\s+TO\s+THE/, "Administration"],

	[/PARALEGAL/, "Paralegal"],
	[/LEGAL (ASSISTANT|SUPPORT|SERVICES)|LITIGATION SUPPORT/, "Legal Support"],

	[/PARTNER/, "Partner"],
	[/\b(SR|SENIOR) COUNSEL/, "Senior Counsel"],
	[/COUNSEL/, "Of Counsel"],
	[/LAW CLERK/, "Law Clerk"],
	// Bare "Attorney" and "ATTORNEY ASSOCIATE" both land here: the firm has no
	// separate unspecified-attorney tier, so they are reported as associates.
	[/ASSOCIATE|ATTORNEY/, "Associate"],

	[
		/\bBILL|ACCOUNT|PAYABLE|RECEIVABLE|\bAR\b|\bAP\b|PAYROLL|COLLECTIONS|CONTROLLER|FINANC/,
		"Billing & Accounting",
	],
	[
		/\bIT\b|TECHNICAL|TECHNOLOGY|SYSTEM|CLOUD|DEVELOPER|ENGINEER|HELP DESK|LITIFY|TABLEAU|INFRASTRUCTURE/,
		"IT",
	],
	[/\bHR\b|HUMAN RESOURCE|RECRUIT|BENEFITS/, "HR & Recruiting"],
	[
		/MARKETING|GROWTH|BRAND|COMMUNICATION|MEDIA|EVENTS|DESIGN|CONTENT|PUBLIC RELATIONS|CLIENT (RELATIONS|ENGAGEMENT|SOLUTIONS)/,
		"Marketing & Client Relations",
	],
];

/**
 * Map a raw `prof_type` onto a title group.
 *
 * Unrecognised titles land in "Administration"; null, empty and whitespace-only
 * values land in "Unspecified" — 174 rows in the demo data have no title at all,
 * and they should not be silently filed as staff.
 */
export function normalizeProfType(
	raw: string | null | undefined,
): ProfTitleGroup {
	const value = (raw ?? "").trim().toUpperCase();
	if (value === "") return "Unspecified";

	for (const [pattern, group] of RULES) {
		if (pattern.test(value)) return group;
	}
	return "Administration";
}

/**
 * Annual billable-hours defaults per group.
 *
 * Starting points the firm overrides in the Goals tab. Non-billing groups get
 * zero rather than a nominal figure — a receptionist measured against 1,800
 * hours makes the whole report look wrong.
 */
export const DEFAULT_ANNUAL_HOURS_BY_GROUP: Record<ProfTitleGroup, number> = {
	Partner: 1700,
	"Senior Counsel": 1800,
	"Of Counsel": 1600,
	Associate: 1850,
	"Law Clerk": 1400,
	Paralegal: 1500,
	"Legal Support": 1400,
	"Billing & Accounting": 0,
	IT: 0,
	"HR & Recruiting": 0,
	"Marketing & Client Relations": 0,
	Administration: 0,
	Unspecified: 0,
};
